import { useMemo } from 'react';
import { stringifyEntityRef, type Entity } from '@backstage/catalog-model';
import { useSearch } from '@backstage/plugin-search-react';
import { Box, Grid, Tag, TagGroup } from '@backstage/ui';
import {
  TechDocsCard,
  CardVariant,
  EntityCard,
  MinimalCard,
} from '@internal/backstage-plugin-hm-react';
import { getMatchingItems } from './featured-items';
import type { QetaKind } from './featured-items';
import { SEARCH_RESULT_TYPE } from './search-tabs';
import { useFeaturedEntities } from './use-featured-entity';

const TechDocsFeaturedCard = ({ entity }: { entity: Entity }) => (
  <TechDocsCard
    actionable
    fullHeight
    withoutTheme
    entity={entity}
    variant={CardVariant.minimal}
  />
);

const EntityFeaturedCard = ({ entity }: { entity: Entity }) => (
  <EntityCard
    actionable
    fullHeight
    withoutTheme
    entity={entity}
    variant={CardVariant.minimal}
  />
);

const QETA_KIND_PLURALS: Record<QetaKind, string> = {
  question: 'questions',
  article: 'articles',
  collection: 'collections',
};

const QetaFeaturedCard = ({
  kind,
  name,
  title,
}: {
  kind: QetaKind;
  name: string;
  title?: string;
}) => (
  <Grid.Item>
    <MinimalCard
      entity={{
        apiVersion: '',
        kind: 'BackOverflow',
        metadata: { name: title ?? `${kind}: ${name}` },
        spec: { type: kind },
      }}
      title={title ?? `${kind}: ${name}`}
      description={`A featured _${kind}_ matching your search query`}
      actionRoute={`/qeta/${QETA_KIND_PLURALS[kind]}/${name}`}
      fullHeight
      withoutTheme
      target="_self"
    />
  </Grid.Item>
);

const useMatchingItems = (term: string, types: string[]) =>
  useMemo(() => getMatchingItems(term, types), [term, types]);

export const FeaturedSearchResults = () => {
  const { term, types } = useSearch();
  const matchingItems = useMatchingItems(term, types);
  const resolvedItems = useFeaturedEntities(matchingItems);

  if (resolvedItems.length === 0) {
    return null;
  }

  return (
    <Box
      style={{
        backgroundColor: 'var(--bui-announcement-bg-subdued)',
        borderRadius: 'var(--bui-radius-2)',
      }}
    >
      <TagGroup>
        <Tag>Featured</Tag>
      </TagGroup>
      <Grid.Root gap="3" columns={{ xs: '1', sm: '2', md: '4' }} p="4">
        {resolvedItems.map(item => {
          if ('entity' in item) {
            const Card =
              item.type === SEARCH_RESULT_TYPE.TECHDOCS
                ? TechDocsFeaturedCard
                : EntityFeaturedCard;
            return (
              <Grid.Item key={stringifyEntityRef(item.entity)}>
                <Card entity={item.entity} />
              </Grid.Item>
            );
          }

          return (
            <QetaFeaturedCard
              key={`${item.kind}-${item.name}`}
              kind={item.kind}
              name={item.name}
              title={item.title}
            />
          );
        })}
      </Grid.Root>
    </Box>
  );
};
